import { SubscriptionType } from '@/@types/enums/SubscriptionType';
import { GUIDES_APP } from '@/helpers/appContext';

type CompanySubscriptions = {
  id?: string;
  paddle?: { subscriptionId?: string | null } | null;
  guidesSubscription?: { subscriptionId?: string | null } | null;
};

/**
 * C7: which product the Plans page should show prices for.
 *
 * On a fresh subscribe visit, the query/session guides context (set by the
 * auth pages) decides, and a company whose last subscription was a Guides one
 * stays on Guides: holding kb as well, it would otherwise see Snaplore prices
 * from the not-paid page, and paying there would end Guides. On a CHANGE-PLAN visit, that context cannot
 * be trusted — a fresh tab has empty sessionStorage, and nothing threads the
 * query through reliably — so it must derive from the company's ACTUAL
 * current subscription tag (`paddle.app`) instead.
 */
export const resolvePlansPageIsGuides = (params: {
  isUpdatePlan: boolean;
  companyPaddleApp?: string | null;
  queryIsGuides: boolean;
  /** Roman, 2026-11-04: `?app=snaplore` from the product switcher names Snaplore outright, whatever else points at Guides. */
  queryIsSnaplore?: boolean;
  /** SNAP-2231: a Guides-only company subscribes to Guides with or without ?app=guides. */
  companyIsGuidesOnly?: boolean;
}): boolean => {
  if (params.isUpdatePlan) return params.companyPaddleApp === GUIDES_APP;
  if (params.queryIsSnaplore) return false;
  return params.queryIsGuides || !!params.companyIsGuidesOnly || params.companyPaddleApp === GUIDES_APP;
};

/**
 * The status the price list judges a company by: any status hides the trial prices. Roman, 2026-11-04: "One Guides
 * trial". A Snaplore history does not count against it, so a first Guides purchase is judged as new.
 */
export const priceListStatus = (params: {
  isUpdatePlan: boolean;
  isGuides: boolean;
  paddleStatus?: SubscriptionType | null;
  paddleApp?: string | null;
  guidesStatus?: SubscriptionType | null;
}): SubscriptionType | null => {
  if (params.isUpdatePlan || !params.isGuides) return params.paddleStatus ?? null;
  if (params.guidesStatus) return params.guidesStatus;
  return params.paddleApp === GUIDES_APP ? params.paddleStatus ?? null : null;
};

/**
 * The company's current subscription among all of its owner's. A company that bought again after a pause has both
 * at Paddle, and the API refuses to change or pause the old one.
 */
export const findCompanySubscription = <T extends { id: string; customData?: { companyId?: string } | null }>(
  subscriptions: T[],
  company?: CompanySubscriptions | null
): T | undefined =>
  subscriptions.find((subscription) => subscription.id === company?.paddle?.subscriptionId) ??
  subscriptions.find(
    (subscription) =>
      subscription.customData?.companyId === company?.id &&
      subscription.id !== company?.guidesSubscription?.subscriptionId
  );

/** Guides bought on top of a Snaplore subscription (SNAP-2241), among all of the owner's subscriptions. */
export const findGuidesSubscription = <T extends { id: string }>(
  subscriptions: T[],
  company?: CompanySubscriptions | null
): T | undefined =>
  subscriptions.find((subscription) => subscription.id === company?.guidesSubscription?.subscriptionId);
